import {acceptHMRUpdate, defineStore} from 'pinia';
import day from "../utils/day";
import {marketWeek} from "../utils";

const ALL_TRADES = 0
const UNUSUAL_TRADES = 1
const WEEKLY_TRADES = 2
const SWING_TRADES = 3
const LEAP_TRADES = 4
const ODTE_TRADES = 5

const TRADE_CALLS = 1
const TRADE_PUTS = 2

const ALL_ORDERS = 0
const SWEEPS_ORDERS = 1
const LARGE_ORDERS = 2
const BLOCK_ORDERS = 3

const getWeeklyExp = (weeksOffset) => {
    let currentDateTime = day().tz('America/New_York');
    if (weeksOffset !== 0 || currentDateTime.day() !== 5) {
        // move to upcoming Friday, then add week offsets
        const dow = currentDateTime.day(); // 0 Sun ... 6 Sat
        const daysUntilFriday = (5 - dow + 7) % 7;
        currentDateTime = currentDateTime.add(daysUntilFriday + (weeksOffset * 7), 'day');
    } else if (weeksOffset !== 0) {
        currentDateTime = currentDateTime.add(weeksOffset * 7, 'day');
    }
    currentDateTime = currentDateTime.endOf('day');
    return currentDateTime.toDate();
}

export const useExchangeStore = defineStore('exchange', {
  state: () => ({
    realtimeEnabled: true,
    premiumAmountFilter: 25000,
    symbol: null,
    contract: null,
    symbols: [],
    excludeSymbols: [],
    tradesTypeFilter: ALL_TRADES,
    tradeTypesTimeframeFilter: 0,
    tradeDirection: 0,
    tradeOrderType: ALL_ORDERS,
    contractPrice: null,
    shouldRefreshTrades: false,
    contractPriceRange: { min: 0, max: 50},
    aboveAsk: false,
    createdAfter: null,
    createdBefore: null,
    expirationAfter: null,
    expirationBefore: null,
    expirations: null,
    hiddenColumns: [],
  }),
  getters: {
    subscribeTradesEnabled: (state) => {
      return true
    },
    selectedSymbols: (state) => state.symbols,
    unusual: (state) => {
      return state.tradesTypeFilter === UNUSUAL_TRADES
    },
    expireTodayEnabled: (state) => {
      return state.tradeTypesTimeframeFilter === ODTE_TRADES
    },
    minCreated: (state) => {
      if (state.createdAfter) {
        return day(state.createdAfter, 'YYYY-MM-DD HH:mm').tz('America/New_York').toDate()
      } else if (state.tradeTypesTimeframeFilter === ODTE_TRADES) {
        return day().tz('America/New_York').startOf("day").toDate();
      }

      return marketWeek().toDate();
    },
    maxCreated: (state) => {
      if (state.createdBefore) {
        return day(state.createdBefore, 'YYYY-MM-DD HH:mm').tz('America/New_York').toDate()
      }
      return null
    },
    onlyExpirations: (state) => {
      if (state.expirations) {
        return state.expirations.map((exp) => day(exp, 'YYYY-MM-DD').toDate())
      }
      return null
    },
    minExpiration: (state) => {
      if (state.expirationAfter) {
        return day(state.expirationAfter, 'YYYY-MM-DD').toDate()
      }
      switch(state.tradeTypesTimeframeFilter) {
        case ODTE_TRADES:
          return day().tz('America/New_York').startOf('day').toDate();
        case WEEKLY_TRADES:
          return day().tz('America/New_York').startOf('week').toDate();
        case SWING_TRADES:
          return getWeeklyExp(0)
        case LEAP_TRADES:
          return getWeeklyExp(24)
      }
      return null
    },
    maxExpiration: (state) => {
      if (state.expirationBefore) {
        return day(state.expirationBefore, 'YYYY-MM-DD').toDate()
      }
      switch(state.tradeTypesTimeframeFilter) {
        case ODTE_TRADES:
          return day().tz('America/New_York').endOf('day').toDate();
        case WEEKLY_TRADES:
          return getWeeklyExp(0)
        case SWING_TRADES:
          return getWeeklyExp(24)
      }
      return null
    },
    tradeDirectionTypeBullish: (state) => {
      return state.tradeDirection === TRADE_CALLS
    },
    tradeDirectionTypeBearish: (state) => {
      return state.tradeDirection === TRADE_PUTS
    },
    minContractPrice: (state) => {
      return state.contractPrice
    }
  },
  actions: {
    setTradeTypesTimeframeFilter(value) {
      this.tradeTypesTimeframeFilter = value
      if (value !== 0) {
        this.expirationAfter = null
        this.expirationBefore = null
        this.expirations = null
      }
    },
    toggleColumn(colName, totalColumns) {
      const idx = this.hiddenColumns.indexOf(colName);
      if (idx >= 0) {
        this.hiddenColumns.splice(idx, 1);
      } else {
        // Don't allow hiding ALL columns
        if (totalColumns - this.hiddenColumns.length <= 1) return;
        this.hiddenColumns.push(colName);
      }
    },
    resetColumns() {
      this.hiddenColumns = [];
    },
  },
},
{
  persistedState: {
    // store options goes here
  },
});

// make sure to pass the right store definition, `useAuth` in this case.
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useExchangeStore, import.meta.hot))
}
